import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { UserX, Clock, Building2, MapPin, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Widget Tableau de bord — Collaborateurs absents (admin / manager).
 *
 * Liste les collaborateurs déclarés absents AUJOURD'HUI via le bouton
 * « Collaborateur absent » du widget Retard de pointage. Affichage temps réel,
 * re-fetch sur l'événement custom `plania:absences-updated`.
 *
 * Permet d'annuler une absence déclarée par erreur (DELETE mark-absent).
 */
export default function AbsentCollaboratorsWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyShiftId, setBusyShiftId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pointage/absences/today");
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onUpdated = () => load();
    window.addEventListener("plania:absences-updated", onUpdated);
    const id = setInterval(load, 60_000);
    return () => {
      window.removeEventListener("plania:absences-updated", onUpdated);
      clearInterval(id);
    };
  }, [load]);

  const handleCancel = async (row) => {
    if (!window.confirm(
      `Annuler l'absence de ${row.collaborateur_name} ?\n\nLe shift redeviendra actif et sortira de cette liste.`
    )) return;
    setBusyShiftId(row.shift_id);
    try {
      await api.delete(`/pointage/late-arrivals/${row.shift_id}/mark-absent`);
      toast.success(`Absence annulée pour ${row.collaborateur_name}`);
      await load();
      window.dispatchEvent(new CustomEvent("plania:absences-updated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'annuler l'absence");
    } finally {
      setBusyShiftId(null);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-sm" data-testid="absent-collaborators-widget">
      <div className="px-4 sm:px-5 py-3 border-b border-zinc-200 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`h-7 w-7 inline-flex items-center justify-center rounded-sm flex-shrink-0 ${
            items.length > 0 ? "bg-red-50 text-red-700 border border-red-200" : "bg-zinc-50 text-zinc-400 border border-zinc-200"
          }`}>
            <UserX size={14} />
          </span>
          <div className="min-w-0">
            <p className="label-uppercase">Collaborateurs absents</p>
            <p className="text-[11px] text-zinc-500 font-mono truncate">Aujourd'hui — déclarés par un manager ou admin</p>
          </div>
        </div>
        <span
          className={`text-[11px] font-mono px-2 py-0.5 rounded-sm flex-shrink-0 ${
            items.length > 0 ? "bg-red-50 text-red-700 border border-red-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"
          }`}
          data-testid="absent-collab-count"
        >
          {items.length} absent{items.length > 1 ? "s" : ""}
        </span>
      </div>

      {loading ? (
        <p className="p-5 text-sm text-zinc-500 font-mono">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="p-5 text-center" data-testid="absent-collab-empty">
          <p className="text-sm text-zinc-700">Aucune absence déclarée aujourd'hui.</p>
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {items.map((r) => (
            <li
              key={r.shift_id}
              className="px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
              data-testid={`absent-collab-${r.shift_id}`}
            >
              <div className="flex-1 min-w-0">
                <Link
                  to={`/collaborateurs/${r.collaborateur_id}/dossier`}
                  className="text-sm font-medium text-zinc-900 hover:text-blue-700 hover:underline underline-offset-2"
                  data-testid={`absent-collab-link-${r.shift_id}`}
                >
                  {r.collaborateur_name}
                </Link>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-0.5 mt-1 text-[11px] font-mono text-zinc-600">
                  <p className="inline-flex items-center gap-1.5">
                    <Clock size={11} className="text-zinc-400" />
                    Shift {r.start_time} → {r.end_time}
                  </p>
                  <p className="inline-flex items-center gap-1.5">
                    <Building2 size={11} className="text-zinc-400" /> {r.brand_name}
                  </p>
                  {r.site_name && (
                    <p className="inline-flex items-center gap-1.5">
                      <MapPin size={11} className="text-zinc-400" /> {r.site_name}
                    </p>
                  )}
                </div>
                {r.marked_by_name && (
                  <p className="text-[10px] text-zinc-400 font-mono mt-1">
                    Déclaré par {r.marked_by_name}
                  </p>
                )}
              </div>
              <button
                onClick={() => handleCancel(r)}
                disabled={busyShiftId === r.shift_id}
                className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-sm border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 disabled:opacity-60 flex-shrink-0 transition-colors"
                title="Annuler l'absence (erreur de saisie)"
                data-testid={`absent-collab-cancel-${r.shift_id}`}
              >
                {busyShiftId === r.shift_id ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <RotateCcw size={12} />
                )}
                Annuler
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
